import type { FunctionComponent, ReactElement } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { useMcpContext } from '../McpContextProvider';
import { bitbucketWorkspaceSelectorMessages } from './messages';
import { orgMismatchMessages } from '../orgMismatchMessages';
import MultiSelect from '../MultiSelect/MultiSelect';
import FieldLabel from '../MultiSelect/FieldLabel';
import {
  getAvatarOptionChipColor,
  getAvatarOptionIcon,
} from '../MultiSelect/avatarOption';
import { grantedNoPermissions } from '../domain/policyState';
import {
  getBitbucketWorkspaceOrgId,
  getOrgOptionState,
} from '../domain/orgMatch';
import type { BitbucketWorkspace } from '#definitions/consent-info';

const BITBUCKET_SELECT_INPUT_ID =
  'oauth-consent-mcp-bitbucket-workspace-select';

/** How many Bitbucket workspaces one consent can select while multi-select is on. */
const MAX_SELECTABLE_BITBUCKET_WORKSPACES = 4;

// Pure functions of the item, so they live at module scope rather than being
// rebuilt in the component body.
const toValue = (ws: BitbucketWorkspace): string => ws.ari;
const getLabel = (ws: BitbucketWorkspace): string => ws.name;

/**
 * Bitbucket workspace picker for the MCP consent screen.
 *
 * Single-select by default; with site/Bitbucket multi-select on it holds up to
 * MAX_SELECTABLE_BITBUCKET_WORKSPACES workspaces, and every selection
 * in either picker has to share one organization.
 *
 * Rendered below the Atlassian site selector whenever /consent/info returned at
 * least one workspace. An empty list hides the field entirely rather than
 * showing one with nothing to pick. There is no
 * `oauth-consent_bitbucket-mcp-workspace` check in this flow — see
 * useBitbucketWorkspaces. The selected workspace's ARI is submitted as
 * an additional `selectedWorkspace` param on Accept (see McpFlowParameters).
 *
 * Hidden as well whenever the Bitbucket check does not speak for the flow, i.e.
 * 'bitbucket' is absent from `getOrderedPolicySources` — which today means a
 * Trello-only token. That list is what the screen aggregates permissions over
 * and what `hasFailedPolicyCheck` / `getPrimaryPolicyErrorSource` are scoped
 * to, so a field rendered outside it would be a target the user can select and
 * submit while every permission it grants is filtered out of the screen, and a
 * failed check for it could reach neither the full-panel alert nor this field's
 * own message slot. The visibility rule is read off that same list rather than
 * off the scopes so the two cannot drift: participation otherwise depends only
 * on /consent/info's workspace list, which knows nothing about scopes. It is
 * computed once, as `showBitbucketSelector`, by getSelectorVisibility, and MCP
 * renders this field only while it is set.
 *
 * A successful check that grants nothing is reported in this field's own message
 * slot, reading only `policies.bitbucket` so a site result never surfaces here.
 * A *failed* request is shown full-panel by McpPolicyAlerts instead, and the
 * field stays mounted throughout so the selection can be changed and retried.
 */
const BitbucketWorkspaceSelector: FunctionComponent =
  (): ReactElement | null => {
    const {
      availableBitbucketWorkspaces,
      selectedBitbucketWorkspaces,
      setSelectedBitbucketWorkspaces,
      policies,
      hasOrgMismatch,
      bitbucketConstraintOrgId,
      isSiteBitbucketMultiSelectEnabled: isMulti,
      isMultiSelectionAllowed,
      isBitbucketBlockedBySandbox,
      hasSandboxConflict,
    } = useMcpContext();
    const maxSelected = isMultiSelectionAllowed
      ? MAX_SELECTABLE_BITBUCKET_WORKSPACES
      : 1;
    const { formatMessage } = useIntl();

    // The messages below take priority over the helpers, so they gate them —
    // and only those messages are errors.
    const noPermissions = grantedNoPermissions(policies.bitbucket);
    const canShowHelp =
      !hasOrgMismatch && !hasSandboxConflict && !noPermissions;

    // Other-org workspaces stay listed but cannot be picked.
    const { isItemDisabled, helpKind } = getOrgOptionState({
      items: availableBitbucketWorkspaces,
      constraintOrgId: bitbucketConstraintOrgId,
      getOrgId: getBitbucketWorkspaceOrgId,
      hasOwnSelection: selectedBitbucketWorkspaces.length > 0,
      isMultiSelect: isMulti,
      canShowHelp,
    });

    // First rule that applies wins: errors, then the org and sandbox rules,
    // and last the default hint stating the cap and the org rule.
    const messageRules: {
      applies: boolean;
      descriptor: MessageDescriptor;
      values?: Record<string, number>;
    }[] = [
      {
        applies: hasOrgMismatch,
        descriptor: orgMismatchMessages.orgMismatch,
      },
      {
        applies: noPermissions,
        descriptor: bitbucketWorkspaceSelectorMessages.noPermissions,
      },
      {
        applies: isBitbucketBlockedBySandbox,
        descriptor: bitbucketWorkspaceSelectorMessages.sandboxBlocked,
      },
      {
        applies:
          isMulti &&
          !isMultiSelectionAllowed &&
          selectedBitbucketWorkspaces.length > 0,
        descriptor: bitbucketWorkspaceSelectorMessages.singleWorkspaceOrg,
      },
      {
        applies: helpKind === 'empty',
        descriptor: bitbucketWorkspaceSelectorMessages.noOrgWorkspaces,
      },
      {
        applies: helpKind === 'constraint',
        descriptor: bitbucketWorkspaceSelectorMessages.orgConstraint,
      },
      {
        applies: isMulti,
        descriptor: bitbucketWorkspaceSelectorMessages.multiSelectHint,
        values: { maxSelected },
      },
    ];
    const rule = messageRules.find(({ applies }) => applies);
    const message = rule && formatMessage(rule.descriptor, rule.values);
    return (
      <MultiSelect<BitbucketWorkspace, string>
        inputId={BITBUCKET_SELECT_INPUT_ID}
        items={availableBitbucketWorkspaces}
        value={selectedBitbucketWorkspaces.map(toValue)}
        toValue={toValue}
        getLabel={getLabel}
        getIcon={getAvatarOptionIcon}
        getChipColor={getAvatarOptionChipColor}
        isItemDisabled={isItemDisabled}
        isMulti={isMulti}
        maxSelected={maxSelected}
        isDisabled={isBitbucketBlockedBySandbox}
        message={message}
        messageTone={canShowHelp ? 'information' : 'error'}
        placeholder={formatMessage(
          bitbucketWorkspaceSelectorMessages.placeholder,
        )}
        renderLabel={() => (
          <FieldLabel>
            {formatMessage(bitbucketWorkspaceSelectorMessages.label)}
          </FieldLabel>
        )}
        onChange={(_aris, items) => setSelectedBitbucketWorkspaces(items)}
      />
    );
  };

export default BitbucketWorkspaceSelector;
